import react, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/store';
import { Question, QuestionResponse, Response, Survey, User } from '../types';
import { checkSurveyResponseEligibility, fetchSurvey, generateSurveyResponse, signInWithGoogle, updateSurveyResponses } from '../Firebase/utils';
import QuestionPreviewDisplay from '../Components/QuestionPreviewDisplay';
import { setResponse as setResponseRedux } from '../store/features/CompleteSurveySlice';
import QuestionResponseDisplay from '../Components/QuestionResponseDisplay';
import Modal from '../Components/Modal';
import TextField from '../Components/TextField';
import { FaGoogle } from 'react-icons/fa';
import { setUser } from '../store/features/UserSlice';
import SignInWithEmailComponent from '../Components/SignInWithEmailComponent';
import { IoMdCheckmark, IoMdClose } from "react-icons/io";

const CompleteSurvey = () => {

    const user = useAppSelector(state => state.user.user);
    const surveyResponse = useAppSelector(state => state.response.response);
    const navigate = useNavigate();
    const [ isEligible, setIsEligible ] = useState<boolean>(true);
    const dispatch = useAppDispatch();
    const [ surveyId, setSurveyId ] = useState<string>(useLocation().pathname.split('/')[2]);
    const [ surveyIdInput, setSurveyIdInput ] = useState<string>('');
    const [ survey, setSurvey ] = useState<Survey>();
    const [ surveyNotFound, setSurveyNotFound ] = useState<boolean>(false);
    const [ unresponded, setUnresponded ] = useState<QuestionResponse[]>([]);
    const [ surveyCompleted, setSurveyCompleted ] = useState<boolean>(false);
    const [ termsAccepted, setTermsAccepted ] = useState<boolean>(false);
    const [ response, setResponse ] = useState<Response>({
        id: '',
        survey: {
            id: '',
            title: '',
            description: '',
            questions: [],
            responses: [],
            creator: '',
            createdAt: new Date(),
            updatedAt: new Date(),
        } as Survey,
        surveyId: '',
        respondant: user.email,
        responses: [],
        createdAt: new Date(),
        completedAt: null,
        duration: null,
    });

    console.log(user);

    const handleGenerateSurvey = async () => {

            const survey = await fetchSurvey(user, surveyId);
            if(survey){
                setSurvey(survey);
            }else{
                setSurveyNotFound(true);
            }
    }

    const handleCheckEligibility = async () => {
        if(survey && user){
            const checkEligibility = await checkSurveyResponseEligibility(user, survey);
            if(checkEligibility){
                setIsEligible(false);
                return;
            }
        }
    }

    const handleSubmitResponse = async () => {

        const unrespondedQuestions = surveyResponse.responses.filter(response => {
            if (typeof response.response === 'string') {
                return response.response === '';
            } else if (Array.isArray(response.response)) {
                return response.response.length === 0;
            } else if (typeof response.response === 'number') {
                return response.response === 0;
            }
            return false;
        });
        setUnresponded(unrespondedQuestions);

        if(survey && user && unrespondedQuestions.length === 0){
            await updateSurveyResponses(user, survey, surveyResponse);
            await generateSurveyResponse(user, survey, surveyResponse);
            setSurveyCompleted(true);
        }
    }

    const handleGenerateResponse = async () => {
        
        if(survey && user){
            dispatch(setResponseRedux({
                ...response,
                survey: survey,
                surveyId: survey.id,
                respondant: user.email,
                responses: survey.questions.map(question => {
                    return {questionId: question.id, questionText: question.questionText, questionType: question.type, response: question.type === 'Multiple Selection' ? [] : question.type === 'rating' || question.type === 'scale' ? 0 : ''};
                }),
            }));
        }
    }

    useEffect(() => {
        if(!survey){
            console.log('Getting Survey...');
            handleGenerateSurvey();
        }
    }, [user, surveyId]);
    
    useEffect(() => {
        if(survey){
            handleCheckEligibility();
        }
    }, [survey, user]);

    useEffect(() => {
        if(survey && user && isEligible){
            handleGenerateResponse();
        }
    }, [survey, user]);

    const handleGoogleSignIn = async () => {
        const {...user} = await signInWithGoogle() as User;
        if (user) {
            dispatch(setUser(user));
        }
    };

   

    if (!survey && !surveyNotFound) {
        return <div className='text-white'>Loading...</div>;
    }

    return (
        <div className='flex flex-col min-h-screen'>
            <header className='flex flex-col md:flex-row justify-center items-center gap-3 md:gap-10 text-[#3da8e2] p-4 pt-20'>
                <div className='flex flex-col gap-4 items-center'>
                  <h1 className='text-[2.7997rem] md:text-8xl font-Velocity tracking-widest text-shadow-lg text-center'>{survey ? survey.title : 'Complete Survey'}</h1>
                  {survey && survey.description && <p className='md:w-1/2 font-BCBold text-gray-500'>{survey.description}</p>}
                </div>
            </header>
            <div className='flex flex-col items-center gap-5 p-5'>
                <section className='flex flex-col gap-10 w-full items-center mt-10 text-white font-BCBold'>
                {survey?.questions?.map((question, index) => {
                    return(
                        <QuestionResponseDisplay key={index} question={question} />
                    );
                })
                }
                </section>
            </div>
            {!surveyNotFound && <button onClick={() => {handleSubmitResponse()}} className='self-center w-11/12 md:w-11/12 mt-10 bg-white text-[#3da8e2] p-4 rounded-md border-[#8dd2f6] border shadow-black shadow-lg font-BardotBold tracking-widest hover:bg-[#3da8e2] hover:text-white'>Submit Survey</button>}
            <Modal title={'Thank You for your Time!'} openState={!isEligible} onClose={() => navigate('/')}>
                <h2 className='mt-5 mb-16 text-white text-2xl font-BCBold'>You have already completed the {survey?.title} Survey. You are only allowed to complete a survey once.</h2>
                <button onClick={() => {navigate('/')}} className='bg-[#3da8e2] text-white p-4 rounded-md border-[#8dd2f6] border shadow-black shadow-lg font-BardotBold tracking-widest hover:bg-white hover:text-[#3da8e2]'>Close</button>
            </Modal>
            <Modal title={'Unanswered Questions'} openState={unresponded.length > 0} onClose={() => {setUnresponded([])}}>
                <div className='flex flex-col gap-5'>
                    {unresponded.map((question, index) => {
                        return (
                            <div key={index} className='flex flex-col gap-2'>
                                <h2 className='text-white text-lg font-BCBold'>{question.questionText}</h2>
                                <p className='text-white text-md font-BCSans'>This question has not been answered.</p>
                            </div>
                        );
                    })}
                    <button onClick={() => {setUnresponded([]); }} className='bg-[#3da8e2] text-white p-4 rounded-md border-[#8dd2f6] border shadow-black shadow-lg font-BardotBold tracking-widest hover:bg-white hover:text-[#3da8e2]'>Back to Survey</button>
                </div>
            </Modal>
            <Modal title={'Survey Completed'} openState={surveyCompleted} onClose={() => {navigate('/')}}>
                <h2 className='mt-5 mb-16 text-white text-2xl font-BCBold'>Thank you for completing the {survey?.title} Survey. Your responses have been recorded.</h2>
                <button onClick={() => {navigate('/')}} className='bg-[#3da8e2] text-white p-4 rounded-md border-[#8dd2f6] border shadow-black shadow-lg font-BardotBold tracking-widest hover:bg-white hover:text-[#3da8e2]'>Close</button>
            </Modal>
            <Modal title={'Survey Not Found'} openState={surveyNotFound} onClose={() => {navigate('/')}}>
                <div className='flex flex-col gap-8'>
                    <h2 className='text-white text-2xl font-BCBold'>Enter a Valid Survey Id</h2>
                    <TextField placeholder='Survey Id' inputValue={surveyIdInput} setInputValue={setSurveyIdInput} />
                    <div className='flex gap-4 md:gap-8'>
                        <button onClick={() => {setSurveyId(surveyIdInput); setSurveyNotFound(false)}} className='flex-grow bg-[#3da8e2] text-white p-4 rounded-md border-[#8dd2f6] border shadow-black shadow-lg font-BardotBold tracking-widest hover:bg-white hover:text-[#3da8e2]'>Submit</button>
                        <button onClick={() => {navigate('/')}} className='flex-grow bg-[#3da8e2] text-white p-4 rounded-md border-[#8dd2f6] border shadow-black shadow-lg font-BardotBold tracking-widest hover:bg-white hover:text-[#3da8e2]'>Close</button>
                    </div>
                </div>
            </Modal>
            <Modal title={'Survey Not Found'} openState={!termsAccepted && !surveyNotFound} onClose={() => {navigate('/')}}>
                <div className='flex flex-col gap-8'>
                    <h2 className='text-white text-2xl font-BCBold'>Survey Terms & Conditions</h2>
                    <h3 className='text-xl text-white tracking-wider'>Before completing the survey, please accept the terms and conditions listed below.</h3>
                    <p className='text-gray-300 font-bold'>
                      By participating in this survey, you agree to the following terms:
                    </p>
                    <p className='text-gray-400'>
                      <span className='font-bold'>Purpose:</span> This survey is intended to collect insights to support research, product development, or community engagement efforts by the survey creator (the “Surveyor”).
                    </p>
                    <p className='text-gray-400'>
                      <span className='font-bold'>Email Collection:</span> By completing this survey, you agree to share your email address with the Surveyor. Your email will only be used for purposes such as beta test invitations or relevant follow-up communication. However, your survey responses are anonymous and cannot be linked to your email address.
                    </p>
                    <p className='text-gray-400'>
                      <span className='font-bold'>Data Access:</span> Only the Surveyor has access to the survey results and collected email addresses. This information will not be shared or sold to any third party.
                    </p>
                    <p className='text-gray-400'>
                      <span className='font-bold'>Privacy Warning:</span> Please do not submit any sensitive personal information, such as your full name, date of birth, phone number, social security number, or home address. This survey is not designed to collect personally identifiable information beyond your email address.
                    </p>
                    <p className='text-gray-400'>
                      <span className='font-bold'>Voluntary Participation:</span> Participation is entirely voluntary. You may exit the survey at any time before submission. Once submitted, your responses will be stored for analysis by the Surveyor.
                    </p>
                    <p className='text-gray-300 font-bold'>
                      By attempting, completing, and/or submitting this survey, you confirm that you understand and accept these terms.
                    </p>
                    <div className='flex gap-4 md:gap-8'>
                        <button onClick={() => {navigate('/')}} className='flex items-center justify-center gap-2 flex-grow bg-[#3da8e2] text-white p-4 rounded-md border-[#8dd2f6] border shadow-black shadow-lg font-BardotBold tracking-widest hover:bg-white hover:text-[#3da8e2]'><IoMdClose size={36}/>Leave</button>
                        <button onClick={() => {setSurveyId(surveyIdInput); setTermsAccepted(true)}} className='flex items-center justify-center gap-2 flex-grow bg-[#3da8e2] text-white p-4 rounded-md border-[#8dd2f6] border shadow-black shadow-lg font-BardotBold tracking-widest hover:bg-white hover:text-[#3da8e2]'><IoMdCheckmark size={36}/>Accept</button>
                    </div>
                </div>
            </Modal>
            <SignInWithEmailComponent isSignInModalOpen={!user.uid} isSignUpModalOpen={false} onClose={() => user.uid ? null : navigate('/')} setIsSignUpModalOpen={() => null}/>
      {/* <Modal title='Please Sign In' onClose={() => { navigate('/') }} openState={!user.uid} left={'-left-5'}>
              <button onClick={() => handleGoogleSignIn()} className='flex flex-row gap-10 w-full items-center justify-center mt-10 py-2 text-lg md:text-xl uppercase font-SoulGaze tracking-wider text-slate-300 border border-slate-400 rounded-md'>
                  <p className='flex gap-4 items-center text-[#3da8e2] font-BardotBold tracking-[0.25rem]'><FaGoogle className='text-2xl'/>Signin With Google</p>
              </button>
      </Modal> */}
        </div>
    );
}

export default CompleteSurvey;